import { DateRange } from "@/components/controls/DateRangePicker";

export interface DateFilterValue {
  dateRange: string | DateRange;
}

export interface NumericFilterValue {
  gt?: number;
  lt?: number;
  eq?: number;
  min?: number;
  max?: number;
}

export interface BooleanFilterValue {
  eq: boolean;
}

export const TEXT_CONDITIONS = [
  "contains",
  "notContains",
  "is",
  "isNot",
  "isEmpty",
  "isNotEmpty"
] as const;

export type TextCondition = (typeof TEXT_CONDITIONS)[number];

// API format for text filters
export type TextFilterValue =
  | { contains_nocase: string }
  | { contains: string }
  | { not_contains_nocase: string }
  | { not_contains: string }
  | { eq: string | null }
  | { ne: string }
  | { is_null: boolean }
  | { not_null: boolean };

// Helper to extract condition and value from API format
export const parseTextFilterValue = (
  filter: TextFilterValue
): { condition: TextCondition; value?: string } => {
  if ("contains_nocase" in filter) {
    return { condition: "contains", value: filter.contains_nocase };
  }
  if ("contains" in filter) {
    return { condition: "contains", value: filter.contains };
  }
  if ("not_contains_nocase" in filter) {
    return { condition: "notContains", value: filter.not_contains_nocase };
  }
  if ("not_contains" in filter) {
    return { condition: "notContains", value: filter.not_contains };
  }
  if ("eq" in filter) {
    if (filter.eq === null) {
      return { condition: "isEmpty" };
    }
    return { condition: "is", value: filter.eq };
  }
  if ("ne" in filter) {
    return { condition: "isNot", value: filter.ne };
  }
  if ("is_null" in filter) {
    return { condition: "isEmpty" };
  }
  if ("not_null" in filter) {
    return { condition: "isNotEmpty" };
  }
  return { condition: "contains", value: "" };
};

// Helper to build API format from condition and value
export const buildTextFilterValue = (
  condition: TextCondition,
  value?: string
): TextFilterValue => {
  switch (condition) {
    case "contains":
      return { contains_nocase: value ?? "" };
    case "notContains":
      return { not_contains_nocase: value ?? "" };
    case "is":
      return { eq: value ?? "" };
    case "isNot":
      return { ne: value ?? "" };
    case "isEmpty":
      return { is_null: true };
    case "isNotEmpty":
      return { not_null: true };
    default:
      return { contains_nocase: value ?? "" };
  }
};

export interface FilterOption {
  value: any;
  label: string;
  icon?: JSX.Element;
}

export const BOOLEAN_FILTER_TRANSLATION_KEY =
  "datatoolbar.filters.booleanOptions";

export const TEXT_FILTER_TRANSLATION_KEY = "datatoolbar.filters.textConditions";
